import { extendRef, tryOnScopeDispose, useStorage, UseStorageOptions } from '@vueuse/core'
import {
  computed,
  MaybeRefOrGetter,
  ref,
  Ref,
  shallowReadonly,
  shallowRef,
  toRef,
  toValue,
  watch,
  watchEffect,
} from 'vue'

export function asyncWritableComputed<T>(
  initialValue: T,
  config: {
    get: () => Promise<T>
    set: (value: T) => Promise<void> | void
  },
) {
  const data = shallowRef(initialValue)

  watchEffect(async () => {
    data.value = await config.get()
  })

  return computed<T>({
    get: () => data.value,
    set: (value: T) => {
      config.set(value)
    },
  })
}

export function computedDebounced<T>(
  value: Ref<T> | (() => T),
  delayBySeconds: MaybeRefOrGetter<number> | ((newValue: T, oldValue: T) => number),
) {
  const debouncedValue = ref<T>(toValue(value)) as Ref<T>
  let timer: ReturnType<typeof setTimeout> | undefined

  watch(toRef(value), (newValue, oldValue) => {
    if (timer) clearTimeout(timer)
    const delay =
      typeof delayBySeconds === 'function'
        ? delayBySeconds(newValue, oldValue)
        : toValue(delayBySeconds)
    if (delay === 0) {
      debouncedValue.value = newValue
    } else {
      timer = setTimeout(() => {
        debouncedValue.value = newValue
      }, delay * 1000)
    }
  })

  return shallowReadonly(debouncedValue)
}

export function useLocalStorage<T extends string | boolean | number | null>(
  key: string | string[],
  value: MaybeRefOrGetter<T>,
  options?: UseStorageOptions<T>,
): Ref<T> {
  const _key = Array.isArray(key) ? key.join(':') : key
  return useStorage<T>(`aura:${_key}`, value, localStorage, options)
}

export type DefaultPreference<T> = Ref<T> & {
  default: T
  applyPreference: (ref: Ref<T>) => void
}

export function useDefaultPreference<T>(
  preference: Ref<T | null>,
  defaultValue: T,
): DefaultPreference<T> {
  const stoppers: Set<() => void> = new Set()
  const wrapper = computed({
    get: () => preference.value ?? defaultValue,
    set: (newValue) => {
      if (newValue === defaultValue) preference.value = null
      else preference.value = newValue
    },
  })

  function applyPreference(ref: Ref<T>) {
    const { stop } = watch(ref, (newValue) => {
      wrapper.value = newValue
    })
    stoppers.add(stop)
    const value = preference.value
    // if there is no preference, we can’t sync it
    if (value === null) return
    // don’t trigger an update if we already have the right value
    if (ref.value === value) return
    // don’t apply the preference if another value was set before
    if (ref.value !== defaultValue) return
    ref.value = value
  }

  tryOnScopeDispose(() => {
    for (const stop of stoppers) stop()
    stoppers.clear()
  })

  return extendRef(wrapper, { default: defaultValue, applyPreference }) as DefaultPreference<T>
}
